import { Directive, ElementRef, HostListener, Input, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
    selector: '[appNumberFormat]'
})
export class NumberFormatDirective implements OnInit {
    @Input() decimals: number = 0; // Número de decimales (0 por defecto)
    @Input() allowNegative: boolean = false; // Permitir números negativos

    private regex: RegExp;
    private specialKeys: Array<string> = ['Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Delete'];

    constructor(
        private el: ElementRef,
        private ngControl: NgControl
    ) { }

    ngOnInit() {
        // Formatear el valor inicial si existe
        if (this.ngControl.value) {
            this.formatValue(this.ngControl.value);
        }
    }

    @HostListener('input', ['$event'])
    onInput(event: any) {
        const input = event.target as HTMLInputElement;
        let value = input.value;

        // Remover todo excepto números, punto decimal y signo negativo
        value = value.replace(/[^0-9.-]/g, '');

        // Si no permite negativos, remover el signo
        if (!this.allowNegative) {
            value = value.replace(/-/g, '');
        }

        // Actualizar el valor del modelo sin formato
        this.ngControl.control?.setValue(value, { emitEvent: false });

        // Formatear el valor visual
        this.formatValue(value);
    }

    @HostListener('blur', ['$event'])
    onBlur(event: any) {
        const input = event.target as HTMLInputElement;
        let value = input.value;

        // Remover formato para obtener el número puro
        value = value.replace(/,/g, '');

        // Actualizar el modelo con el valor sin formato
        this.ngControl.control?.setValue(value, { emitEvent: false });

        // Formatear el valor visual
        this.formatValue(value);
    }

    @HostListener('focus', ['$event'])
    onFocus(event: any) {
        // Opcional: remover formato al hacer foco para facilitar edición
        // Descomenta las siguientes líneas si prefieres este comportamiento
        /*
        const input = event.target as HTMLInputElement;
        let value = input.value.replace(/,/g, '');
        input.value = value;
        */
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        // Permitir teclas especiales
        if (this.specialKeys.indexOf(event.key) !== -1) {
            return;
        }

        // Permitir Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        if (event.ctrlKey || event.metaKey) {
            return;
        }

        const input = event.target as HTMLInputElement;
        const current: string = input.value;
        const position = input.selectionStart;
        const next: string = [current.slice(0, position), event.key, current.slice(position)].join('');

        // Validar el input
        if (next && !this.validate(next)) {
            event.preventDefault();
        }
    }

    private validate(value: string): boolean {
        // Remover comas para validar
        value = value.replace(/,/g, '');

        // Permitir campo vacío
        if (value === '' || value === '-') {
            return true;
        }

        // Validar formato numérico
        if (this.decimals > 0) {
            // Con decimales
            const regex = this.allowNegative
                ? new RegExp(`^-?\\d*\\.?\\d{0,${this.decimals}}$`)
                : new RegExp(`^\\d*\\.?\\d{0,${this.decimals}}$`);
            return regex.test(value);
        } else {
            // Sin decimales (solo enteros)
            const regex = this.allowNegative ? /^-?\d*$/ : /^\d*$/;
            return regex.test(value);
        }
    }

    private formatValue(value: any) {
        if (!value && value !== 0) {
            this.el.nativeElement.value = '';
            return;
        }

        // Convertir a string y remover comas existentes
        let stringValue = value.toString().replace(/,/g, '');

        // Separar parte entera y decimal
        const parts = stringValue.split('.');
        let integerPart = parts[0];
        const decimalPart = parts[1];

        // Manejar signo negativo
        const isNegative = integerPart.startsWith('-');
        if (isNegative) {
            integerPart = integerPart.substring(1);
        }

        // Formatear la parte entera con comas
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        // Reconstruir el número
        let formattedValue = isNegative ? '-' + integerPart : integerPart;

        // Agregar parte decimal si existe
        if (decimalPart !== undefined) {
            formattedValue += '.' + decimalPart.substring(0, this.decimals);
        }

        // Actualizar el valor visual del input
        this.el.nativeElement.value = formattedValue;
    }
}
